import React from "react";
import { AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import type { UnmatchedSkuData } from "../hooks/useConsumptionReport";

interface ReportingUnmatchedSkusProps {
  loading: boolean;
  data: UnmatchedSkuData[];
  shopifyDomain?: string;
  canBackfill?: boolean;
  backfillLoading?: boolean;
  backfillDisabled?: boolean;
  onBackfill?: () => void;
}

export const ReportingUnmatchedSkus: React.FC<ReportingUnmatchedSkusProps> = ({
  loading,
  data,
  shopifyDomain,
  canBackfill = false,
  backfillLoading = false,
  backfillDisabled = false,
  onBackfill,
}) => {
  const totalOccurrences = data.reduce((sum, item) => sum + item.occurrenceCount, 0);

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="bg-card rounded-lg border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-100 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Unmatched SKUs</h3>
            <p className="text-sm text-muted-foreground">
              SKUs from Shopify orders that couldn't be matched to inventory
            </p>
          </div>
        </div>

        {!loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-2xl font-bold">{data.length}</p>
              <p className="text-sm text-muted-foreground">Unique SKUs</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-2xl font-bold">{totalOccurrences}</p>
              <p className="text-sm text-muted-foreground">Total Occurrences</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4 md:col-span-1 col-span-2">
              <p className="text-2xl font-bold">
                {data.length > 0 ? data.reduce((sum, item) => sum + item.orderCount, 0) : 0}
              </p>
              <p className="text-sm text-muted-foreground">Affected Orders</p>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg border">
        <div className="p-6 border-b flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold">SKU Details</h3>
            <p className="text-sm text-muted-foreground">
              Review unmatched SKUs and create rules or add them to inventory
            </p>
          </div>
          {canBackfill && onBackfill && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBackfill}
              disabled={backfillDisabled || backfillLoading}
            >
              {backfillLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {backfillLoading ? "Backfilling..." : "Backfill Unmatched"}
            </Button>
          )}
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Occurrences</TableHead>
                <TableHead className="text-right">Orders</TableHead>
                <TableHead>First Seen</TableHead>
                <TableHead>Last Seen</TableHead>
                <TableHead>Sample Orders</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  </TableRow>
                ))
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <div className="p-3 bg-green-100 rounded-full">
                        <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="font-medium">All SKUs Matched</p>
                      <p className="text-sm text-muted-foreground">
                        No unmatched SKUs found for the selected date range
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((item) => (
                  <TableRow key={item.sku}>
                    <TableCell>
                      <code className="px-2 py-1 bg-muted rounded text-sm font-mono">
                        {item.sku}
                      </code>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={item.occurrenceCount > 10 ? "destructive" : item.occurrenceCount > 3 ? "secondary" : "outline"}>
                        {item.occurrenceCount}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {item.orderCount}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(item.firstSeen).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(item.lastSeen).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {item.sampleOrderIds.slice(0, 3).map((orderId) => (
                          shopifyDomain ? (
                            <a
                              key={orderId}
                              href={`https://${shopifyDomain}/admin/orders/${orderId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              #{orderId}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span key={orderId} className="text-xs text-muted-foreground">
                              #{orderId}
                            </span>
                          )
                        ))}
                        {item.sampleOrderIds.length > 3 && (
                          <span className="text-xs text-muted-foreground">
                            +{item.sampleOrderIds.length - 3} more
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Help text */}
      {!loading && data.length > 0 && (
        <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-1">How to fix unmatched SKUs:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Add a <strong>SKU Rule</strong> to transform the Shopify SKU to match your inventory format</li>
            <li>Create the <strong>missing SKU</strong> in your inventory if it's a new product</li>
            <li>Check for <strong>typos or formatting issues</strong> in Shopify product variants</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default ReportingUnmatchedSkus;
